/**
 * Escalation drafting.
 *
 * Select findings, preview the message, copy it. There is no send: the platform has no
 * mail integration, and an affordance that looked like one would leave the user
 * believing a supplier had been written to when nobody had.
 *
 * The selection offers only service levels the platform can evidence. The server
 * refuses a vendor-attested level regardless, but offering one here and refusing it
 * afterwards would teach the user that the rule is negotiable.
 */

import { useMemo, useState } from "react";
import { Notice, Section } from "../components/Chrome";
import { draftEscalation } from "../lib/api";
import { money, recordCount } from "../lib/format";
import type { EscalationDraft as Draft, ServiceLevel, Vendor } from "../lib/types";

/** The service levels a draft may be built from, in the order the vendor screen lists them. */
export function escalable(vendor: Vendor): ServiceLevel[] {
  return vendor.service_levels.filter((level) => reasonWithheld(vendor, level) === null);
}

/**
 * Why a service level is absent from the selection, or null when it is offered.
 *
 * The same three tests the server applies, stated here so the omission is visible where
 * the choice is made. A selector silently showing three of six service levels reads as
 * a defect; the provenance rule is the product and belongs on screen.
 */
export function reasonWithheld(vendor: Vendor, level: ServiceLevel): string | null {
  if (!level.claimable) {
    return "vendor-attested, so there is no independent measurement to put to the supplier";
  }
  const finding = vendor.findings.find((f) => f.service_level_id === level.id);
  if (finding === undefined || finding.computed === null) {
    return "not independently measured this period";
  }
  if (finding.computed_met !== false) {
    return "meeting its contractual target on our own figure";
  }
  return null;
}

export function withheld(vendor: Vendor): { level: ServiceLevel; reason: string }[] {
  return vendor.service_levels
    .map((level) => ({ level, reason: reasonWithheld(vendor, level) }))
    .filter((entry): entry is { level: ServiceLevel; reason: string } => entry.reason !== null);
}

/** Material findings are pre-selected: they are the ones worth a supplier's attention. */
function defaultSelection(vendor: Vendor): string[] {
  const levels = escalable(vendor);
  const material = levels.filter((level) =>
    vendor.findings.some((f) => f.service_level_id === level.id && f.material),
  );
  return (material.length > 0 ? material : levels).map((level) => level.id);
}

export function EscalationDraft({ vendor }: { vendor: Vendor }) {
  const levels = useMemo(() => escalable(vendor), [vendor]);
  const omitted = useMemo(() => withheld(vendor), [vendor]);
  const [selected, setSelected] = useState<string[]>(() => defaultSelection(vendor));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function toggle(id: string) {
    setDraft(null);
    setCopied(false);
    setSelected((current) =>
      current.includes(id) ? current.filter((other) => other !== id) : [...current, id],
    );
  }

  async function preview() {
    setPending(true);
    setError(null);
    setCopied(false);
    try {
      setDraft(await draftEscalation(vendor.id, selected));
    } catch (caught) {
      setDraft(null);
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : "The draft could not be composed.",
      );
    } finally {
      setPending(false);
    }
  }

  async function copy() {
    if (!draft) return;
    await navigator.clipboard?.writeText(`${draft.subject}\n\n${draft.body}`);
    setCopied(true);
  }

  if (levels.length === 0) {
    return (
      <Section
        title="Escalation"
        subtitle="A draft is composed only from service levels the platform measured itself."
      >
        <Notice>
          No service level on this vendor currently misses its target on the computed
          figure, so there is nothing to put to the supplier. The vendor-attested service
          levels are excluded whatever their figures say — there is no independent
          measurement behind them and no claim can rest on one.
        </Notice>
      </Section>
    );
  }

  return (
    <Section
      title="Escalation"
      subtitle="Compose a message the supplier can be held to: both figures, the records behind ours, the measurement rule the two readings differ on, and the credit implied."
    >
      <fieldset
        className="card"
        style={{ padding: "14px 18px 16px", border: "1px solid var(--line)", margin: 0 }}
      >
        <legend style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)", padding: "0 6px" }}>
          FINDINGS TO INCLUDE
        </legend>
        <div style={{ display: "grid", gap: 8 }}>
          {levels.map((level) => {
            const finding = vendor.findings.find((f) => f.service_level_id === level.id);
            return (
              <label
                key={level.id}
                style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13 }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(level.id)}
                  onChange={() => toggle(level.id)}
                  style={{ marginTop: 3, accentColor: "var(--gold)" }}
                />
                <span>
                  <span className="serif" style={{ fontSize: 14.5 }}>
                    {level.name}
                  </span>
                  <span style={{ display: "block", fontSize: 11.5, color: "var(--slate)", marginTop: 2 }}>
                    vendor {finding?.reported ?? "—"}
                    {level.unit} · computed{" "}
                    <span style={{ color: "var(--gold)" }}>
                      {finding?.computed}
                      {level.unit}
                    </span>{" "}
                    from {recordCount(finding?.record_count ?? 0)} ·{" "}
                    {money(finding?.credit_computed ?? 0)} credit
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {omitted.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Notice>
            <strong style={{ color: "var(--parchment)" }}>Not available to escalate</strong>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {omitted.map((entry) => (
                <li key={entry.level.id} style={{ marginBottom: 4 }}>
                  <span style={{ color: "var(--parchment)" }}>{entry.level.name}</span> is{" "}
                  {entry.reason}.
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
        <button
          onClick={() => void preview()}
          disabled={pending || selected.length === 0}
          style={{
            background: "var(--gold)",
            border: "none",
            borderRadius: 8,
            padding: "9px 18px",
            color: "var(--ink)",
            fontSize: 13,
            fontWeight: 600,
            opacity: pending || selected.length === 0 ? 0.5 : 1,
          }}
        >
          {pending ? "Composing…" : "Preview draft"}
        </button>
        <span style={{ fontSize: 11.5, color: "var(--slate)" }}>
          {selected.length} of {levels.length} selected
        </span>
      </div>

      {error && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="warn">{error}</Notice>
        </div>
      )}

      {draft && (
        <div style={{ marginTop: 16 }}>
          <div className="card" style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.07em", color: "var(--slate)" }}>
              SUBJECT
            </div>
            <div className="serif" style={{ fontSize: 15, marginTop: 4, marginBottom: 14 }}>
              {draft.subject}
            </div>
            <pre
              aria-label="Escalation draft"
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontFamily: "var(--sans)",
                fontSize: 12.5,
                lineHeight: 1.7,
                color: "var(--parchment)",
              }}
            >
              {draft.body}
            </pre>
          </div>

          <div
            style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}
          >
            <button
              onClick={() => void copy()}
              style={{
                background: "transparent",
                border: "1px solid var(--gold)",
                borderRadius: 8,
                padding: "8px 16px",
                color: "var(--gold)",
                fontSize: 12.5,
              }}
            >
              {copied ? "Copied" : "Copy draft"}
            </button>
            <span style={{ fontSize: 11.5, color: "var(--slate)" }}>
              Credit claimed in this draft{" "}
              <span className="num" style={{ color: "var(--gold)" }}>
                {money(draft.credit_computed)}
              </span>{" "}
              against {money(draft.credit_reported)} conceded
            </span>
          </div>

          <div style={{ marginTop: 12 }}>
            <Notice>
              Nothing is sent from here. There is no mail integration behind this screen,
              so the draft is copied out and sent by whoever owns the relationship.
            </Notice>
          </div>

          {draft.excluded.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Notice>
                <strong style={{ color: "var(--parchment)" }}>
                  Kept out of the draft
                </strong>
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {draft.excluded.map((exclusion) => (
                    <li key={exclusion.service_level_id} style={{ marginBottom: 4 }}>
                      {exclusion.explanation}
                    </li>
                  ))}
                </ul>
              </Notice>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
